import { defineStore } from "pinia";
import { ref, computed } from "vue";

const MAX_EMBEDDED = 5;

export interface CachedEmbeddedApp {
  key: string;
  url: string;
  label: string;
  lastUsed: number;
}

export const useAppOrchestratorStore = defineStore("appOrchestrator", () => {
  const cachedEmbeddedApps = ref<CachedEmbeddedApp[]>([]);
  const activeEmbeddedKey = ref<string | null>(null);

  const activeEmbedded = computed(() =>
    cachedEmbeddedApps.value.find((e) => e.key === activeEmbeddedKey.value)
  );

  function ensureEmbedded(key: string, url: string, label = "") {
    const now = Date.now();
    const existing = cachedEmbeddedApps.value.find((e) => e.key === key);
    if (existing) {
      existing.url = url;
      existing.label = label || existing.label;
      existing.lastUsed = now;
      activeEmbeddedKey.value = key;
      return;
    }
    if (cachedEmbeddedApps.value.length >= MAX_EMBEDDED) {
      evictLRUEmbedded();
    }
    cachedEmbeddedApps.value.push({ key, url, label, lastUsed: now });
    activeEmbeddedKey.value = key;
  }

  function evictLRUEmbedded() {
    if (cachedEmbeddedApps.value.length === 0) return;
    const sorted = [...cachedEmbeddedApps.value].sort(
      (a, b) => a.lastUsed - b.lastUsed
    );
    const lru = sorted[0];
    cachedEmbeddedApps.value = cachedEmbeddedApps.value.filter(
      (e) => e.key !== lru.key
    );
    if (activeEmbeddedKey.value === lru.key) {
      activeEmbeddedKey.value =
        cachedEmbeddedApps.value.length > 0
          ? cachedEmbeddedApps.value[cachedEmbeddedApps.value.length - 1].key
          : null;
    }
  }

  function setActiveEmbeddedKey(key: string | null) {
    activeEmbeddedKey.value = key;
  }

  /** Call when leaving an embedded route so we don't show an iframe in the pool (optional; pool visibility is toggled by parent). */
  function clearActiveEmbedded() {
    activeEmbeddedKey.value = null;
  }

  /** Force remove one app from pool (e.g. explicit refresh). */
  function evictByKey(key: string) {
    cachedEmbeddedApps.value = cachedEmbeddedApps.value.filter(
      (e) => e.key !== key
    );
    if (activeEmbeddedKey.value === key) {
      activeEmbeddedKey.value = null;
    }
  }

  return {
    cachedEmbeddedApps,
    activeEmbeddedKey,
    activeEmbedded,
    ensureEmbedded,
    evictLRUEmbedded,
    setActiveEmbeddedKey,
    clearActiveEmbedded,
    evictByKey,
    maxEmbedded: MAX_EMBEDDED,
  };
});
